import React, { useState, useEffect, useCallback } from 'react';
import outboundService, {
  BenchmarkReport,
  MetricVerdict,
  OutboundContext,
  LemlistCampaignOption,
  LemlistCampaignStats,
} from '../services/outboundService';
import signalCampaignService, { SignalCampaign } from '../services/signalCampaignService';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/ui';
import { useKampagnenZuordnung, KampagneVerweis } from '../vertrieb/kampagnen';
import { routeById } from '../navigation/routes';

type Mode = 'manual' | 'lemlist' | 'signal';

const LEVEL_COLORS: Record<string, string> = {
  bad: 'var(--sec-danger)',
  average: 'var(--sec-warning)',
  good: 'var(--sec-success)',
  great: 'var(--sec-info)',
  exceptional: '#7c3aed',
  info: 'var(--sec-fg-muted)',
};

const CHANNEL_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '— Kanalmix —' },
  { value: 'email', label: 'Nur E-Mail' },
  { value: 'email_first', label: 'E-Mail-first' },
  { value: 'linkedin_email', label: 'LinkedIn + E-Mail' },
  { value: 'linkedin_first', label: 'LinkedIn-first' },
  { value: 'linkedin_email_call', label: 'LinkedIn + E-Mail + Call' },
];

const PERSONA_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '— Persona —' },
  { value: 'ic', label: 'Individual Contributor' },
  { value: 'manager', label: 'Manager' },
  { value: 'vp', label: 'VP' },
  { value: 'clevel', label: 'C-Level' },
  { value: 'tech', label: 'Tech' },
  { value: 'sales', label: 'Sales' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'hr', label: 'HR' },
];

const ReportView: React.FC<{ report: BenchmarkReport }> = ({ report }) => (
  <div style={{ marginTop: '16px' }}>
    {report.expectedReplyRate !== undefined && (
      <p style={{ color: 'var(--sec-fg-muted)', fontSize: '13px', marginBottom: '12px' }}>
        Erwartete Reply-Rate für diesen Kontext: <strong>~{report.expectedReplyRate}%</strong>
      </p>
    )}
    {report.verdicts.map((v: MetricVerdict) => (
      <div
        className="card"
        key={v.metric}
        style={{ borderLeft: `4px solid ${LEVEL_COLORS[v.level] || 'var(--sec-fg-muted)'}`, marginBottom: '12px' }}
      >
        <div className="card-header">
          <div className="card-title">
            {v.emoji} {v.metric}: {v.value}%
          </div>
          <span style={{ color: LEVEL_COLORS[v.level], fontWeight: 600, fontSize: '13px' }}>
            {v.judgment}
          </span>
        </div>
        <div style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', marginTop: '6px' }}>
          <div>
            <strong>Benchmark:</strong> {v.benchmark}
          </div>
          {v.rootCause && (
            <div style={{ marginTop: '4px' }}>
              <strong>Ursache:</strong> {v.rootCause}
            </div>
          )}
          {v.fix && (
            <div style={{ marginTop: '4px', color: 'var(--sec-fg)' }}>
              <strong>Fix:</strong> {v.fix}
            </div>
          )}
        </div>
      </div>
    ))}
    {report.summary && (
      <div className="alert alert-success" style={{ marginTop: '8px' }}>
        {report.summary}
      </div>
    )}
  </div>
);

const OutboundAnalysisPage: React.FC = () => {
  const zuordnung = useKampagnenZuordnung();
  const [mode, setMode] = useState<Mode>('manual');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<BenchmarkReport | null>(null);

  // manual mode
  const [form, setForm] = useState({
    sent: '',
    opened: '',
    replied: '',
    bounced: '',
    positiveReplies: '',
    linkedinSent: '',
    linkedinAccepted: '',
    messagesSent: '',
    messagesRead: '',
    channelMix: '',
    persona: '',
    listSize: '',
    steps: '',
  });

  // lemlist mode
  const [lemlistCampaigns, setLemlistCampaigns] = useState<LemlistCampaignOption[]>([]);
  const [lemlistId, setLemlistId] = useState('');
  const [lemlistPersona, setLemlistPersona] = useState('');
  const [lemlistStats, setLemlistStats] = useState<LemlistCampaignStats | null>(null);

  // signal mode
  const [signalCampaigns, setSignalCampaigns] = useState<SignalCampaign[]>([]);
  const [signalId, setSignalId] = useState('');
  const [signalVariants, setSignalVariants] = useState<
    { id: string; label: string; angle?: string; sent: number; replied: number; replyRate: number }[]
  >([]);

  const num = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v));

  const reset = () => {
    setReport(null);
    setError('');
    setLemlistStats(null);
    setSignalVariants([]);
  };

  const loadModeData = useCallback(async (m: Mode) => {
    try {
      if (m === 'lemlist' && lemlistCampaigns.length === 0) {
        setLemlistCampaigns(await outboundService.getLemlistCampaigns());
      } else if (m === 'signal' && signalCampaigns.length === 0) {
        setSignalCampaigns(await signalCampaignService.getCampaigns());
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Kampagnen');
    }
  }, [lemlistCampaigns.length, signalCampaigns.length]);

  useEffect(() => {
    loadModeData(mode);
  }, [mode, loadModeData]);

  const runManual = async () => {
    try {
      setLoading(true);
      reset();
      const context: OutboundContext = {
        channelMix: (form.channelMix || undefined) as any,
        persona: (form.persona || undefined) as any,
        listSize: num(form.listSize),
        steps: num(form.steps),
      };
      const result = await outboundService.benchmark(
        {
          sent: num(form.sent),
          opened: num(form.opened),
          replied: num(form.replied),
          bounced: num(form.bounced),
          positiveReplies: num(form.positiveReplies),
          linkedinSent: num(form.linkedinSent),
          linkedinAccepted: num(form.linkedinAccepted),
          messagesSent: num(form.messagesSent),
          messagesRead: num(form.messagesRead),
        },
        context
      );
      setReport(result);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler bei der Auswertung');
    } finally {
      setLoading(false);
    }
  };

  const runLemlist = async () => {
    if (!lemlistId) {
      setError('Bitte eine Lemlist-Kampagne wählen');
      return;
    }
    try {
      setLoading(true);
      reset();
      const { stats, report: rep } = await outboundService.getLemlistStats(lemlistId, lemlistPersona || undefined);
      setLemlistStats(stats);
      setReport(rep);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Lemlist-Statistiken');
    } finally {
      setLoading(false);
    }
  };

  const runSignal = async () => {
    if (!signalId) {
      setError('Bitte eine Signal-Kampagne wählen');
      return;
    }
    try {
      setLoading(true);
      reset();
      const data = await outboundService.getSignalCampaignBenchmark(signalId);
      setSignalVariants(data.variants);
      setReport(data.report);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler bei der Auswertung der Signal-Kampagne');
    } finally {
      setLoading(false);
    }
  };

  const numberInput = (key: keyof typeof form, label: string, placeholder = '') => (
    <div className="form-group" style={{ flex: '1 1 120px' }}>
      <label className="form-label">{label}</label>
      <input
        type="number"
        className="form-input"
        value={form[key]}
        placeholder={placeholder}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </div>
  );

  return (
    <div className="page-container">
      <PageHeader title="Kampagnen-Kennzahlen (Outbound)" subtitle={routeById('outbound-analysis')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Klare Verdicts statt „kommt drauf an": Kennzahlen werden gegen echte lemlist-Benchmarks
        (244K+ Kampagnen, 249M+ E-Mails) bewertet — powered by Skill <em>outbound-analyst</em>.
        Werte manuell eingeben, live aus einer Lemlist-Kampagne ziehen oder eine Signal-Kampagne auswerten.
      </p>

      <div className="btn-group" style={{ marginBottom: '24px' }}>
        <button className={`btn ${mode === 'manual' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMode('manual')}>
          Manuell
        </button>
        <button className={`btn ${mode === 'lemlist' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMode('lemlist')}>
          Lemlist live
        </button>
        <button className={`btn ${mode === 'signal' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMode('signal')}>
          Signal-Kampagne
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {mode === 'manual' && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
          <h2 className="form-section-title">Kennzahlen eingeben</h2>
          <p style={{ fontSize: '12px', color: 'var(--sec-fg-muted)', marginBottom: '12px' }}>
            Absolute Zahlen eingeben (Raten werden berechnet). Mindestens „Versendet" + eine weitere Zahl.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {numberInput('sent', 'Versendet', 'z.B. 1000')}
            {numberInput('opened', 'Geöffnet')}
            {numberInput('replied', 'Antworten')}
            {numberInput('bounced', 'Bounces')}
            {numberInput('positiveReplies', 'Positive Antworten')}
            {numberInput('linkedinSent', 'LinkedIn-Einladungen')}
            {numberInput('linkedinAccepted', 'Einladungen angenommen')}
          </div>
          <p style={{ fontSize: '12px', color: 'var(--sec-fg-muted)', margin: '8px 0 4px' }}>
            Lesequote (gesamt): gelesene Nachrichten über E-Mail + LinkedIn / versendete Nachrichten.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {numberInput('messagesSent', 'Nachrichten gesendet (gesamt)')}
            {numberInput('messagesRead', 'Nachrichten gelesen (gesamt)')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
            <div className="form-group" style={{ flex: '1 1 160px' }}>
              <label className="form-label">Kanalmix</label>
              <select className="form-input" value={form.channelMix} onChange={(e) => setForm({ ...form, channelMix: e.target.value })}>
                {CHANNEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ flex: '1 1 160px' }}>
              <label className="form-label">Persona</label>
              <select className="form-input" value={form.persona} onChange={(e) => setForm({ ...form, persona: e.target.value })}>
                {PERSONA_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {numberInput('listSize', 'Listengröße')}
            {numberInput('steps', 'Schritte')}
          </div>
          <button className="btn btn-success" onClick={runManual} disabled={loading}>
            {loading ? 'Werte aus…' : 'Auswerten'}
          </button>
        </div>
      )}

      {mode === 'lemlist' && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
          <h2 className="form-section-title">Live aus Lemlist</h2>
          <div className="form-group">
            <label className="form-label">Kampagne</label>
            <select className="form-input" value={lemlistId} onChange={(e) => setLemlistId(e.target.value)}>
              <option value="">— Lemlist-Kampagne wählen —</option>
              {lemlistCampaigns.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Persona (optional)</label>
            <select className="form-input" value={lemlistPersona} onChange={(e) => setLemlistPersona(e.target.value)}>
              {PERSONA_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-success" onClick={runLemlist} disabled={loading}>
            {loading ? 'Lade Aktivitäten…' : 'Statistiken laden & auswerten'}
          </button>
          {lemlistStats && lemlistId && (
            <KampagneVerweis lemlistId={lemlistId} zuordnung={zuordnung} leads className="mt-3" />
          )}
          {lemlistStats && (
            <p style={{ fontSize: '12px', color: 'var(--sec-fg-muted)', marginTop: '12px' }}>
              {lemlistStats.totalActivities} Aktivitäten · Versendet {lemlistStats.sent} · Geöffnet{' '}
              {lemlistStats.opened} · Antworten {lemlistStats.replied} · Bounces {lemlistStats.bounced}
              <br />
              Gelesen gesamt {lemlistStats.messagesRead}/{lemlistStats.messagesSent}
              {lemlistStats.messagesSent > 0 &&
                ` (${Math.round((lemlistStats.messagesRead / lemlistStats.messagesSent) * 1000) / 10}%)`}
              {' · '}
              Einladungen {lemlistStats.linkedinAccepted}/{lemlistStats.linkedinSent} angenommen
              {lemlistStats.linkedinSent > 0 &&
                ` (${Math.round((lemlistStats.linkedinAccepted / lemlistStats.linkedinSent) * 1000) / 10}% · Ziel 50%)`}
            </p>
          )}
        </div>
      )}

      {mode === 'signal' && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
          <h2 className="form-section-title">Signal-Kampagne auswerten</h2>
          <div className="form-group">
            <label className="form-label">Kampagne</label>
            <select className="form-input" value={signalId} onChange={(e) => setSignalId(e.target.value)}>
              <option value="">— Signal-Kampagne wählen —</option>
              {signalCampaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-success" onClick={runSignal} disabled={loading}>
            {loading ? 'Werte aus…' : 'Auswerten'}
          </button>
          {signalVariants.length > 0 && signalId && (
            <p className="text-sm mt-3"><Link to={`/signal-campaigns/${encodeURIComponent(signalId)}`}>Signal-Kampagne öffnen</Link></p>
          )}
          {signalVariants.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--sec-fg-subtle)', marginBottom: '4px' }}>
                A/B-VARIANTEN
              </div>
              {signalVariants.map((v) => (
                <div key={v.id} style={{ fontSize: '13px', color: 'var(--sec-fg-muted)' }}>
                  Variante {v.label}: {v.replyRate}% Reply ({v.replied}/{v.sent})
                  {v.angle ? ` · ${v.angle}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {report && <ReportView report={report} />}
    </div>
  );
};

export default OutboundAnalysisPage;
